import React, { useState } from "react";
import { createPortal } from "react-dom";
import Barcode from "react-barcode";
import { rupee } from "../lib/api";
import { X, Printer } from "lucide-react";

// Popular Amazon India thermal label roll sizes for retail barcodes
export const ROLL_SIZES = [
  { id: "50x25", label: "50 × 25 mm (2-up, most common)", w: 50, h: 25, cols: 2, barcodeH: 32, fontSize: 9 },
  { id: "38x25", label: "38 × 25 mm (small)", w: 38, h: 25, cols: 1, barcodeH: 30, fontSize: 8 },
  { id: "50x15", label: "50 × 15 mm (jewellery/tag)", w: 50, h: 15, cols: 1, barcodeH: 22, fontSize: 7 },
  { id: "75x50", label: "75 × 50 mm (large)", w: 75, h: 50, cols: 1, barcodeH: 55, fontSize: 11 },
  { id: "100x50", label: "100 × 50 mm (wide, apparel)", w: 100, h: 50, cols: 2, barcodeH: 55, fontSize: 12 },
  { id: "a4-3x8", label: "A4 sheet · 24 labels (3 × 8)", w: 210, h: 297, cols: 3, sheet: true, barcodeH: 45, fontSize: 10 },
];

export default function BulkBarcodePrintModal({ items, onClose, defaultRoll = "50x25" }) {
  const [rollId, setRollId] = useState(defaultRoll);
  const [showLabels, setShowLabels] = useState(true);
  const roll = ROLL_SIZES.find((r) => r.id === rollId);

  const doPrint = () => {
    const styleId = "vastrakiran-print-page-size";
    document.getElementById(styleId)?.remove();
    const style = document.createElement("style");
    style.id = styleId;
    if (roll.sheet) {
      style.innerHTML = `@page { size: ${roll.w}mm ${roll.h}mm; margin: 6mm; } .bulk-label { page-break-inside: avoid; }`;
    } else {
      style.innerHTML = `@page { size: ${roll.w}mm ${roll.h}mm; margin: 0; } .bulk-label { page-break-after: always; } .bulk-label:last-child { page-break-after: auto; }`;
    }
    document.head.appendChild(style);
    setTimeout(() => {
      window.print();
      setTimeout(() => document.getElementById(styleId)?.remove(), 500);
    }, 150);
  };

  const printArea = (
    <div id="bulk-print-area" className="bulk-print-area">
      {roll.sheet ? (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${roll.cols}, 1fr)`, gap: "3mm" }}>
          {items.map((p, i) => (
            <LabelBody key={`${p.id || p.barcode}-${i}`} p={p} showLabels={showLabels} roll={roll} />
          ))}
        </div>
      ) : (
        items.map((p, i) => (
          <div
            key={`${p.id || p.barcode}-${i}`}
            className="bulk-label"
            style={{ width: `${roll.w}mm`, height: `${roll.h}mm`, boxSizing: "border-box", padding: "1mm", overflow: "hidden" }}
          >
            <LabelBody p={p} showLabels={showLabels} roll={roll} />
          </div>
        ))
      )}
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4 no-print" onClick={onClose}>
        <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-6 w-full max-w-2xl" data-testid="bulk-print-modal">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="font-display font-bold text-xl">Print {items.length} barcode{items.length !== 1 ? "s" : ""}</h2>
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>Choose your Amazon label roll size. Sheet sizes print on regular A4.</p>
            </div>
            <button type="button" onClick={onClose} aria-label="Close"><X size={22} /></button>
          </div>

          <label className="text-sm font-medium">Label / roll size</label>
          <select data-testid="bulk-roll-select" value={rollId} onChange={(e) => setRollId(e.target.value)} className="mt-1 w-full h-12 px-3 rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }}>
            {ROLL_SIZES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>

          <label className="flex items-center gap-2 text-sm mt-3">
            <input type="checkbox" checked={showLabels} onChange={(e) => setShowLabels(e.target.checked)} data-testid="bulk-show-labels" />
            Show name &amp; price on label
          </label>

          <div className="mt-4 p-3 rounded-md text-sm" style={{ background: "var(--surface-muted)", color: "var(--text-muted)" }}>
            Total labels to print: <b className="num">{items.length}</b> · Sheet size: <b>{roll.w} × {roll.h} mm</b>
          </div>

          <div className="flex gap-3 mt-5">
            <button type="button" onClick={onClose} className="h-11 px-5 rounded-md border" style={{ borderColor: "var(--border-soft)" }}>Cancel</button>
            <button type="button" data-testid="bulk-print-go" onClick={doPrint} className="h-11 px-6 rounded-md text-white font-semibold tactile-btn inline-flex items-center gap-2" style={{ background: "var(--primary)" }}>
              <Printer size={16} /> Send to printer
            </button>
          </div>
        </div>
      </div>

      {createPortal(printArea, document.body)}
    </>
  );
}

function LabelBody({ p, showLabels, roll }) {
  if (!p?.barcode) return null;
  return (
    <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
      {showLabels && (
        <div style={{ fontSize: `${roll.fontSize}px`, fontWeight: 700, lineHeight: 1.1, maxWidth: "100%", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
          {p.name}{p.size ? ` · ${p.size}` : ""}{p.color ? ` · ${p.color}` : ""}
        </div>
      )}
      <Barcode value={p.barcode} height={roll.barcodeH} fontSize={roll.fontSize} width={1.4} margin={0} displayValue />
      {showLabels && <div style={{ fontSize: `${roll.fontSize + 1}px`, fontWeight: 700 }}>{rupee(p.price)}</div>}
    </div>
  );
}
